import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, parseISO, differenceInCalendarDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { Plus, CheckCheck, Loader2, ChevronLeft, ChevronRight, Check, X } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Calendar } from './ui/calendar';
import { Badge } from './ui/badge';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const MONTH_NAMES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const WEEKDAYS = ['Dom','Lun','Mar','Mié','Jue','Vie','Sáb'];

// Fixed colors for holiday states (match reference legend)
const NATIONAL_BG = '#fecaca';        // light red — Festivos Nacionales
const LOCAL_APPROVED_BG = '#fca5a5';  // stronger red — Festivos Locales Aprobado
const LOCAL_PENDING_BG = '#fdba74';   // orange — Festivos Locales Pendiente
const LOCAL_REJECTED_BG = '#d1d5db';  // gray — Festivos Locales Rechazado
const PENDING_PERMIT_BG = '#fbbf24';  // orange — Permisos pendientes

const withAlpha = (hex, alpha) => {
  const c = hex.replace('#','');
  const r = parseInt(c.substring(0,2), 16);
  const g = parseInt(c.substring(2,4), 16);
  const b = parseInt(c.substring(4,6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

/**
 * Annual multicolor team vacation grid. Renders 12 months with all workers'
 * vacation days color-coded plus national/local holidays. Superadmin sees an
 * "APROBAR TODAS" button that bulk-approves every pending request.
 *
 * This is a bare component — no <Header/> and no page wrapper — so it can be
 * embedded inside admin tabs as well as inside the standalone /vacation-calendar page.
 */
const TeamVacationGrid = () => {
  const { token } = useAuth();
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ workers: [], vacation_days: [], national_holidays: [], local_holidays: [], is_superadmin: false });
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ mode: 'range', start: '', end: '', notes: '', selectedDays: [] });
  const [submitting, setSubmitting] = useState(false);
  const [approvingAll, setApprovingAll] = useState(false);
  const [approvingKey, setApprovingKey] = useState('');
  // Feb 2026 — controlled reject dialog with reason (replaces window.prompt).
  const [rejectDialog, setRejectDialog] = useState({ open: false, req: null, reason: '' });

  const fetchData = async (year) => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/worker/time-tracking/team-calendar?year=${year}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      console.error('Team calendar fetch failed:', err);
      toast.error('No se pudo cargar el calendario');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(selectedYear); }, [token, selectedYear]);

  const dayIndex = useMemo(() => {
    const idx = {};
    const workerById = Object.fromEntries((data.workers || []).map(w => [w.id, w]));
    for (const vd of (data.vacation_days || [])) {
      const key = vd.date;
      if (!idx[key]) idx[key] = { workers: [], national: null, local: null };
      const w = workerById[vd.worker_id];
      if (!w) continue;
      idx[key].workers.push({
        id: w.id,
        name: w.name,
        color: w.color,
        status: vd.status,
        day_id: vd.id,
        notes: vd.notes || '',
      });
    }
    for (const h of (data.national_holidays || [])) {
      const key = h.date;
      if (!idx[key]) idx[key] = { workers: [], national: null, local: null };
      idx[key].national = h;
    }
    for (const h of (data.local_holidays || [])) {
      const key = h.date;
      if (!idx[key]) idx[key] = { workers: [], national: null, local: null };
      idx[key].local = h;
    }
    return idx;
  }, [data]);

  // Feb 2026 — helpers to identify non-working days for the vacation picker.
  // Vacation counts only Mon-Fri, excluding national + local holidays for
  // the worker's own comunidad. This mirrors the backend rule so the UI
  // never lets the worker "waste" a Sat/Sun/festivo click.
  const holidayDateSet = useMemo(() => {
    const s = new Set();
    for (const h of (data.national_holidays || [])) s.add(h.date);
    const myCom = data.my_comunidad || '';
    for (const h of (data.local_holidays || [])) {
      if (!myCom || h.comunidad === myCom) s.add(h.date);
    }
    return s;
  }, [data]);

  const isNonWorkday = (d) => {
    if (!(d instanceof Date)) return false;
    const wd = d.getDay(); // 0 sunday .. 6 saturday
    if (wd === 0 || wd === 6) return true;
    const iso = format(d, 'yyyy-MM-dd');
    return holidayDateSet.has(iso);
  };

  const countWorkdaysInRange = (startStr, endStr) => {
    if (!startStr) return 0;
    try {
      const start = parseISO(startStr);
      const end = endStr ? parseISO(endStr) : start;
      return eachDayOfInterval({ start, end }).filter((d) => !isNonWorkday(d)).length;
    } catch { return 0; }
  };

  const submitAdd = async () => {
    setSubmitting(true);
    try {
      let dates = [];
      if (addForm.mode === 'multi') {
        if (!addForm.selectedDays.length) {
          toast.error('Selecciona al menos un día en el calendario');
          setSubmitting(false);
          return;
        }
        dates = addForm.selectedDays
          .map((d) => (d instanceof Date ? format(d, 'yyyy-MM-dd') : format(parseISO(d), 'yyyy-MM-dd')))
          .sort();
      } else {
        if (!addForm.start) {
          toast.error('Selecciona una fecha de inicio');
          setSubmitting(false);
          return;
        }
        const start = parseISO(addForm.start);
        const end = addForm.end ? parseISO(addForm.end) : start;
        dates = eachDayOfInterval({ start, end }).map(d => format(d, 'yyyy-MM-dd'));
      }
      const resp = await axios.post(`${API}/api/worker/time-tracking/request-vacation`, {
        dates,
        notes: addForm.notes || '',
      }, { headers: { Authorization: `Bearer ${token}` } });
      const created = (resp.data?.requests || []).length;
      const skipped = resp.data?.skipped || [];
      if (created > 0) {
        toast.success(`Solicitud enviada · ${created} día(s) laborables`);
      }
      if (skipped.length > 0) {
        toast.info(`Se descartaron ${skipped.length} fecha(s): ${skipped.map(s => `${s.date} (${s.reason})`).join(', ')}`);
      }
      setShowAdd(false);
      setAddForm({ mode: 'range', start: '', end: '', notes: '', selectedDays: [] });
      await fetchData(selectedYear);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Error al solicitar los días';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const approveAll = async () => {
    const pendingIds = (data.vacation_days || []).filter(d => d.status === 'pendiente').map(d => d.id);
    if (!pendingIds.length) { toast.info('No hay solicitudes pendientes'); return; }
    if (!window.confirm(`¿Aprobar TODAS las ${pendingIds.length} solicitudes pendientes?`)) return;
    setApprovingAll(true);
    try {
      await axios.put(`${API}/api/admin/time-tracking/vacation-days/approve-bulk`, {
        day_ids: pendingIds,
        approve: true,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${pendingIds.length} solicitudes aprobadas`);
      await fetchData(selectedYear);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al aprobar');
    } finally {
      setApprovingAll(false);
    }
  };

  // Group consecutive pending days per worker (same notes) into a single "request".
  // Backend stores one row per day but users request whole ranges, so we collapse them
  // for the "Solicitudes Pendientes" table.
  const pendingRequests = useMemo(() => {
    const workerById = Object.fromEntries((data.workers || []).map(w => [w.id, w]));
    const pendings = (data.vacation_days || [])
      .filter(d => d.status === 'pendiente')
      .sort((a, b) => (a.worker_id + a.date).localeCompare(b.worker_id + b.date));
    const groups = [];
    let current = null;
    for (const d of pendings) {
      const worker = workerById[d.worker_id];
      if (!worker) continue;
      const dObj = parseISO(d.date);
      const canExtend =
        current &&
        current.worker_id === d.worker_id &&
        (current.notes || '') === (d.notes || '') &&
        differenceInCalendarDays(dObj, current.lastDate) === 1;
      if (canExtend) {
        current.days.push(d);
        current.day_ids.push(d.id);
        current.lastDate = dObj;
      } else {
        current = {
          key: `${d.worker_id}-${d.date}`,
          worker_id: d.worker_id,
          worker,
          notes: d.notes || '',
          days: [d],
          day_ids: [d.id],
          firstDate: dObj,
          lastDate: dObj,
        };
        groups.push(current);
      }
    }
    return groups;
  }, [data]);

  const approveRequest = async (req) => {
    setApprovingKey(req.key);
    try {
      await axios.put(`${API}/api/admin/time-tracking/vacation-days/approve-bulk`, {
        day_ids: req.day_ids,
        approve: true,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Aprobada · ${req.worker.name} · ${req.day_ids.length} día(s)`);
      await fetchData(selectedYear);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al aprobar');
    } finally {
      setApprovingKey('');
    }
  };

  const rejectRequest = (req) => {
    // Open the controlled reject dialog so the superadmin can type a reason.
    setRejectDialog({ open: true, req, reason: '' });
  };

  const confirmReject = async () => {
    const req = rejectDialog.req;
    if (!req) return;
    setApprovingKey(req.key);
    try {
      await axios.put(`${API}/api/admin/time-tracking/vacation-days/approve-bulk`, {
        day_ids: req.day_ids,
        approve: false,
        reject_reason: rejectDialog.reason || '',
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Rechazada · ${req.worker.name}`);
      setRejectDialog({ open: false, req: null, reason: '' });
      await fetchData(selectedYear);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al rechazar');
    } finally {
      setApprovingKey('');
    }
  };

  const approveSingleDay = async (dayId) => {
    try {
      await axios.put(
        `${API}/api/admin/time-tracking/vacation-days/${dayId}/approve`,
        { approve: true, reject_reason: '' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Día aprobado');
      await fetchData(selectedYear);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al aprobar');
    }
  };

  const rejectSingleDay = async (dayId) => {
    const reason = window.prompt('Motivo del rechazo (opcional):', '');
    if (reason === null) return;
    try {
      await axios.put(
        `${API}/api/admin/time-tracking/vacation-days/${dayId}/approve`,
        { approve: false, reject_reason: reason || '' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Día rechazado');
      await fetchData(selectedYear);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al rechazar');
    }
  };

  const yearsToShow = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1];
  const pendingCount = (data.vacation_days || []).filter(d => d.status === 'pendiente').length;

  return (
    <div data-testid="team-vacation-grid">
      {/* Feb 2026 — "Mi resumen" del trabajador logueado */}
      {data.my_vacation && (
        <Card className="mb-4 border-primary/30 bg-gradient-to-r from-primary/5 to-transparent">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4 justify-between">
              <div>
                <div className="text-xs font-medium text-slate-500 mb-1">
                  Tu resumen · {data.my_vacation.worker_name}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className="bg-white">
                    Total: <span className="ml-1 font-bold">{data.my_vacation.total_days}</span>
                  </Badge>
                  <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                    Aprobados: <span className="ml-1 font-bold">{data.my_vacation.used_days}</span>
                  </Badge>
                  <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                    Pendientes: <span className="ml-1 font-bold">{data.my_vacation.pending_days}</span>
                  </Badge>
                  <Badge
                    className={
                      data.my_vacation.remaining_days > 0
                        ? 'bg-blue-100 text-blue-800 hover:bg-blue-100'
                        : 'bg-red-100 text-red-800 hover:bg-red-100'
                    }
                  >
                    Restantes: <span className="ml-1 font-bold">{data.my_vacation.remaining_days}</span>
                  </Badge>
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs font-medium text-slate-500 mb-1">
                  Festivos aplicables · {data.my_comunidad || 'Sin comunidad'}
                </div>
                <div className="flex flex-wrap items-center gap-1.5 justify-end">
                  <Badge variant="outline" className="bg-white">
                    Nacionales: <span className="ml-1 font-bold">{(data.national_holidays || []).length}</span>
                  </Badge>
                  <Badge variant="outline" className="bg-white">
                    Locales ({data.my_comunidad || '—'}):{' '}
                    <span className="ml-1 font-bold">
                      {(data.local_holidays || []).filter((h) => !data.my_comunidad || h.comunidad === data.my_comunidad).length}
                    </span>
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Legend */}
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="text-xs font-medium text-slate-500 mb-2">Leyenda</div>
          <div className="flex flex-wrap gap-2 items-center">
            {(data.workers || []).map((w) => (
              <span
                key={w.id}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium text-white shadow-sm"
                style={{ backgroundColor: withAlpha(w.color, 0.85) }}
                data-testid={`legend-worker-${w.id}`}
              >
                {w.name}
              </span>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 items-center mt-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium text-white" style={{ backgroundColor: PENDING_PERMIT_BG }}>
              Permisos pendientes
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium text-slate-800" style={{ backgroundColor: NATIONAL_BG }}>
              Festivos Nacionales
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium text-white" style={{ backgroundColor: LOCAL_APPROVED_BG }}>
              Festivos Locales (Aprobado)
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium text-slate-800" style={{ backgroundColor: LOCAL_PENDING_BG }}>
              Festivos Locales (Pendiente)
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium text-slate-700" style={{ backgroundColor: LOCAL_REJECTED_BG }}>
              Festivos Locales (Rechazado)
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Pending requests table — superadmin only, moved above calendar for visibility (Feb 2026) */}
      {data.is_superadmin && (
        <Card className="mb-4 border-amber-200" data-testid="vacation-pending-table">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div>
                <div className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                  Solicitudes Pendientes
                  {pendingRequests.length > 0 && (
                    <span className="inline-flex items-center justify-center h-5 min-w-[1.25rem] px-1.5 rounded-full text-[11px] font-semibold bg-amber-500 text-white">
                      {pendingRequests.length}
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500">
                  {pendingRequests.length > 0
                    ? 'Aprueba o rechaza cada solicitud con un click. Usa APROBAR TODAS para hacerlo en bloque.'
                    : 'No hay solicitudes pendientes ahora mismo.'}
                </div>
              </div>
              {pendingRequests.length > 0 && (
                <Button
                  onClick={approveAll}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  disabled={approvingAll}
                  data-testid="vacation-approve-all-inline"
                  size="sm"
                >
                  {approvingAll ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCheck className="h-4 w-4 mr-1" />}
                  Aprobar todas ({pendingRequests.length})
                </Button>
              )}
            </div>
            {pendingRequests.length === 0 ? (
              <div className="text-center py-6 text-sm text-slate-400 border rounded-lg bg-slate-50/50">
                Sin solicitudes pendientes.
              </div>
            ) : (
              <div className="overflow-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-slate-50">
                      <th className="text-left h-9 px-3 font-medium text-slate-600">Trabajador</th>
                      <th className="text-left h-9 px-3 font-medium text-slate-600">Rango</th>
                      <th className="text-left h-9 px-3 font-medium text-slate-600">Días</th>
                      <th className="text-left h-9 px-3 font-medium text-slate-600">Notas</th>
                      <th className="text-right h-9 px-3 font-medium text-slate-600">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingRequests.map((req) => {
                      const rangeStr = req.days.length === 1
                        ? format(req.firstDate, "d MMM yyyy", { locale: es })
                        : `${format(req.firstDate, 'd MMM', { locale: es })} → ${format(req.lastDate, 'd MMM yyyy', { locale: es })}`;
                      const busy = approvingKey === req.key;
                      return (
                        <tr key={req.key} className="border-b hover:bg-slate-50" data-testid={`pending-row-${req.key}`}>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: req.worker.color }} />
                              <span className="font-medium">{req.worker.name}</span>
                            </div>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">{rangeStr}</td>
                          <td className="px-3 py-2">{req.days.length}</td>
                          <td className="px-3 py-2 max-w-[240px] truncate" title={req.notes}>
                            {req.notes || <span className="text-slate-400">—</span>}
                          </td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-green-700 hover:bg-green-100 h-8"
                              onClick={() => approveRequest(req)}
                              disabled={busy}
                              data-testid={`request-approve-${req.key}`}
                            >
                              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}
                              Aprobar
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-700 hover:bg-red-100 h-8"
                              onClick={() => rejectRequest(req)}
                              disabled={busy}
                              data-testid={`request-reject-${req.key}`}
                            >
                              <X className="h-4 w-4 mr-1" />
                              Rechazar
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Actions row */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Button
          onClick={() => setShowAdd(true)}
          className="bg-primary hover:bg-primary/90"
          data-testid="vacation-add-btn"
        >
          <Plus className="h-4 w-4 mr-1" /> Agregar Permiso
        </Button>

        <div className="ml-auto flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => setSelectedYear((y) => y - 1)} data-testid="vacation-year-prev">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          {yearsToShow.map((y) => (
            <button
              key={y}
              onClick={() => setSelectedYear(y)}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                y === selectedYear ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
              }`}
              data-testid={`vacation-year-${y}`}
            >
              {y}
            </button>
          ))}
          <Button variant="ghost" size="icon" onClick={() => setSelectedYear((y) => y + 1)} data-testid="vacation-year-next">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {data.is_superadmin && (
          <Button
            onClick={approveAll}
            className="bg-red-700 hover:bg-red-800 text-white"
            disabled={approvingAll || pendingCount === 0}
            data-testid="vacation-approve-all"
          >
            {approvingAll ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCheck className="h-4 w-4 mr-1" />}
            APROBAR TODAS LAS LICENCIAS {pendingCount > 0 ? `(${pendingCount})` : ''}
          </Button>
        )}
      </div>

      {/* Calendar grid */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 12 }, (_, m) => {
            const first = new Date(selectedYear, m, 1);
            const days = eachDayOfInterval({ start: startOfMonth(first), end: endOfMonth(first) });
            const leadingBlanks = getDay(first); // 0=Sun
            return (
              <Card key={m} className="overflow-hidden">
                <CardContent className="p-3">
                  <div className="text-center text-sm font-semibold text-slate-700 mb-2">
                    {MONTH_NAMES[m]} {selectedYear}
                  </div>
                  <div className="grid grid-cols-7 gap-0.5 text-[10px] font-medium text-slate-500 mb-1">
                    {WEEKDAYS.map((w) => <div key={w} className="text-center py-0.5">{w}</div>)}
                  </div>
                  <div className="grid grid-cols-7 gap-0.5">
                    {Array.from({ length: leadingBlanks }, (_, i) => (
                      <div key={`blank-${i}`} />
                    ))}
                    {days.map((d) => {
                      const key = format(d, 'yyyy-MM-dd');
                      const info = dayIndex[key];
                      let bg = '';
                      let color = '#334155';
                      let title = format(d, "d 'de' MMMM 'de' yyyy", { locale: es });
                      if (info) {
                        if (info.workers.length > 0) {
                          const approved = info.workers.find(w => w.status === 'aprobado');
                          const pending = info.workers.find(w => w.status === 'pendiente');
                          if (approved) {
                            bg = withAlpha(approved.color, 0.85);
                            color = 'white';
                            title += ` — ${approved.name} (aprobado)`;
                          } else if (pending) {
                            bg = withAlpha(pending.color, 0.35);
                            color = '#334155';
                            title += ` — ${pending.name} (pendiente)`;
                          }
                          if (info.workers.length > 1) {
                            title += ` +${info.workers.length - 1} más`;
                          }
                        } else if (info.local) {
                          if (info.local.status === 'aprobado') { bg = LOCAL_APPROVED_BG; color = 'white'; }
                          else if (info.local.status === 'rechazado') { bg = LOCAL_REJECTED_BG; color = '#475569'; }
                          else { bg = LOCAL_PENDING_BG; color = '#7c2d12'; }
                          title += ` — ${info.local.name || 'Festivo local'} (${info.local.status || 'pendiente'})`;
                        } else if (info.national) {
                          bg = NATIONAL_BG;
                          color = '#7f1d1d';
                          title += ` — ${info.national.name || 'Festivo nacional'}`;
                        }
                      }
                      const hasInfo = info && (info.workers.length > 0 || info.national || info.local);
                      const cell = (
                        <div
                          title={title}
                          className={`aspect-square flex items-center justify-center text-[11px] font-medium rounded-sm select-none ${hasInfo ? 'cursor-pointer hover:ring-2 hover:ring-slate-400 transition-shadow' : ''}`}
                          style={{ backgroundColor: bg || 'transparent', color }}
                          data-testid={`day-${key}`}
                        >
                          {format(d, 'd')}
                        </div>
                      );
                      if (!hasInfo) return <div key={key}>{cell}</div>;
                      return (
                        <Popover key={key}>
                          <PopoverTrigger asChild>{cell}</PopoverTrigger>
                          <PopoverContent className="w-72" data-testid={`day-popover-${key}`}>
                            <div className="text-sm font-semibold text-slate-800 mb-2">
                              {format(d, "EEEE d 'de' MMMM 'de' yyyy", { locale: es })}
                            </div>
                            {info.national && (
                              <div className="text-xs text-red-800 bg-red-50 rounded px-2 py-1 mb-2">
                                🇪🇸 Festivo Nacional: {info.national.name || '—'}
                              </div>
                            )}
                            {info.local && (
                              <div className="text-xs text-orange-800 bg-orange-50 rounded px-2 py-1 mb-2">
                                📍 Festivo Local: {info.local.name || '—'} ({info.local.status || 'pendiente'})
                              </div>
                            )}
                            {info.workers.length === 0 ? (
                              <div className="text-xs text-slate-500 italic">Ningún trabajador registrado este día.</div>
                            ) : (
                              <div className="space-y-2">
                                {info.workers.map((w) => (
                                  <div key={w.day_id} className="flex items-center gap-2">
                                    <span
                                      className="inline-block w-2 h-2 rounded-full shrink-0"
                                      style={{ backgroundColor: w.color }}
                                    />
                                    <span className="text-sm flex-1 truncate">{w.name}</span>
                                    <span className={`text-[10px] px-2 py-0.5 rounded ${
                                      w.status === 'aprobado' ? 'bg-green-100 text-green-800' :
                                      w.status === 'rechazado' ? 'bg-red-100 text-red-800' :
                                      'bg-amber-100 text-amber-800'
                                    }`}>{w.status}</span>
                                    {data.is_superadmin && w.status === 'pendiente' && (
                                      <>
                                        <Button
                                          size="icon"
                                          variant="ghost"
                                          className="h-6 w-6 text-green-600 hover:bg-green-100"
                                          onClick={() => approveSingleDay(w.day_id)}
                                          data-testid={`day-approve-${w.day_id}`}
                                        >
                                          <Check className="h-3.5 w-3.5" />
                                        </Button>
                                        <Button
                                          size="icon"
                                          variant="ghost"
                                          className="h-6 w-6 text-red-600 hover:bg-red-100"
                                          onClick={() => rejectSingleDay(w.day_id)}
                                          data-testid={`day-reject-${w.day_id}`}
                                        >
                                          <X className="h-3.5 w-3.5" />
                                        </Button>
                                      </>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </PopoverContent>
                        </Popover>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add permission dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="vacation-add-dialog">
          <DialogHeader>
            <DialogTitle>Nuevo permiso / vacaciones</DialogTitle>
          </DialogHeader>

          {/* Feb 2026 — Toggle entre rango consecutivo y selección múltiple
              de días sueltos. El backend acepta lista de fechas siempre. */}
          <div className="flex bg-slate-100 rounded-md p-1 mb-1 text-xs" data-testid="vacation-mode-toggle">
            <button
              type="button"
              onClick={() => setAddForm({ ...addForm, mode: 'range' })}
              className={`flex-1 py-1.5 rounded transition-colors ${
                addForm.mode === 'range' ? 'bg-white shadow-sm font-semibold text-slate-900' : 'text-slate-600 hover:text-slate-900'
              }`}
              data-testid="vacation-mode-range"
            >
              Rango consecutivo
            </button>
            <button
              type="button"
              onClick={() => setAddForm({ ...addForm, mode: 'multi' })}
              className={`flex-1 py-1.5 rounded transition-colors ${
                addForm.mode === 'multi' ? 'bg-white shadow-sm font-semibold text-slate-900' : 'text-slate-600 hover:text-slate-900'
              }`}
              data-testid="vacation-mode-multi"
            >
              Días sueltos (marca varios)
            </button>
          </div>

          <div className="space-y-3">
            {addForm.mode === 'range' ? (
              <>
                <div>
                  <Label htmlFor="vac-start">Desde</Label>
                  <Input
                    id="vac-start"
                    type="date"
                    value={addForm.start}
                    onChange={(e) => setAddForm({ ...addForm, start: e.target.value })}
                    data-testid="vacation-add-start"
                  />
                </div>
                <div>
                  <Label htmlFor="vac-end">Hasta (opcional — si es un solo día déjalo vacío)</Label>
                  <Input
                    id="vac-end"
                    type="date"
                    value={addForm.end}
                    onChange={(e) => setAddForm({ ...addForm, end: e.target.value })}
                    data-testid="vacation-add-end"
                  />
                </div>
                {addForm.start && (
                  <div className="text-xs text-slate-600 bg-blue-50 border border-blue-200 rounded px-2 py-1.5" data-testid="vacation-range-preview">
                    {(() => {
                      const wd = countWorkdaysInRange(addForm.start, addForm.end);
                      const total = addForm.end
                        ? Math.max(1, Math.round((parseISO(addForm.end) - parseISO(addForm.start)) / 86400000) + 1)
                        : 1;
                      const skipped = total - wd;
                      return (
                        <>
                          Se contarán <strong>{wd}</strong> día(s) laborables.
                          {skipped > 0 && ` Se descartarán ${skipped} (fin de semana o festivo).`}
                        </>
                      );
                    })()}
                  </div>
                )}
              </>
            ) : (
              <div>
                <Label>Marca los días (haz click en cada uno)</Label>
                <div className="border rounded-md p-1 bg-white flex justify-center">
                  <Calendar
                    mode="multiple"
                    selected={addForm.selectedDays}
                    onSelect={(days) => setAddForm({ ...addForm, selectedDays: days || [] })}
                    locale={es}
                    weekStartsOn={1}
                    numberOfMonths={1}
                    disabled={[
                      { before: new Date() },
                      (d) => isNonWorkday(d),
                    ]}
                    modifiers={{ holiday: (d) => holidayDateSet.has(format(d, 'yyyy-MM-dd')) }}
                    modifiersClassNames={{ holiday: 'text-red-600 font-semibold' }}
                    data-testid="vacation-add-multi-calendar"
                  />
                </div>
                <div className="text-xs text-slate-500 mt-2 flex flex-wrap items-center gap-1.5">
                  <span>
                    {(addForm.selectedDays || []).length} día(s) laborable(s) seleccionado(s).
                  </span>
                  {(addForm.selectedDays || []).length > 0 && (
                    <button
                      type="button"
                      onClick={() => setAddForm({ ...addForm, selectedDays: [] })}
                      className="text-red-600 hover:underline"
                      data-testid="vacation-add-clear-days"
                    >
                      Limpiar
                    </button>
                  )}
                </div>
                <div className="text-[10px] text-slate-400 mt-1">
                  Los fines de semana y festivos aparecen deshabilitados — las vacaciones solo cuentan días laborables.
                </div>
                {(addForm.selectedDays || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {[...addForm.selectedDays]
                      .sort((a, b) => new Date(a) - new Date(b))
                      .map((d, idx) => (
                        <Badge
                          key={idx}
                          variant="outline"
                          className="text-[10px] bg-primary/10 border-primary/30"
                        >
                          {format(new Date(d), 'dd/MM')}
                        </Badge>
                      ))}
                  </div>
                )}
              </div>
            )}
            <div>
              <Label htmlFor="vac-notes">Notas (opcional)</Label>
              <Textarea
                id="vac-notes"
                rows={2}
                placeholder="Motivo, tipo de permiso, etc."
                value={addForm.notes}
                onChange={(e) => setAddForm({ ...addForm, notes: e.target.value })}
                data-testid="vacation-add-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)} disabled={submitting}>Cancelar</Button>
            <Button
              onClick={submitAdd}
              disabled={submitting}
              data-testid="vacation-add-submit"
            >
              {submitting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              Enviar solicitud
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject dialog with reason (Feb 2026) */}
      <Dialog
        open={rejectDialog.open}
        onOpenChange={(o) => !o && setRejectDialog({ open: false, req: null, reason: '' })}
      >
        <DialogContent className="sm:max-w-md" data-testid="vacation-reject-dialog">
          <DialogHeader>
            <DialogTitle>
              Rechazar solicitud{rejectDialog.req ? ` · ${rejectDialog.req.worker.name}` : ''}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="reject-reason">Motivo (opcional)</Label>
            <Textarea
              id="reject-reason"
              rows={3}
              placeholder="Ej: coincide con otro compañero, período crítico, etc."
              value={rejectDialog.reason}
              onChange={(e) => setRejectDialog({ ...rejectDialog, reason: e.target.value })}
              data-testid="vacation-reject-reason"
            />
            <div className="text-xs text-slate-500">
              El trabajador recibirá un email con este motivo y se marcarán los días como rechazados.
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRejectDialog({ open: false, req: null, reason: '' })}
              disabled={!!approvingKey}
            >
              Cancelar
            </Button>
            <Button
              onClick={confirmReject}
              disabled={!!approvingKey}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="vacation-reject-submit"
            >
              {approvingKey ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <X className="h-4 w-4 mr-1" />}
              Rechazar solicitud
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamVacationGrid;
